import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ListSupportAdminTicketsPriorityEnum,
  ListSupportAdminTicketsScopeEnum,
  ListSupportAdminTicketsStatusEnum,
  ListSupportAdminTicketsTierEnum,
} from '../../../../libs/api-client/src/generated/api/support-admin-api';
import {
  fetchSupportAgents,
  fetchSupportTickets,
  toFilterPriority,
  toFilterStatus,
  toFilterTier,
  type SupportQueueFilters,
} from '../lib/support-api';
import { supportEnumLabel } from '../lib/support-enum-labels';
import { useDebouncedValue } from '../lib/useDebouncedValue';
import { PageBody, PageHead } from '../components/Page';
import { StatusBanner } from '../components/StatusBanner';
import { Badge, TONE_TEXT, type Tone } from '../components/Pill';
import { formatCount } from '../i18n/formats';
import { useLocale } from '../i18n/LocaleProvider';

const PAGE_SIZE = 25;

// From the generated client rather than hand-typed: the narrowing helpers below already
// validate against these enums, so a hand-written list can only ever disagree with them.
const STATUSES = Object.values(ListSupportAdminTicketsStatusEnum);
const TIERS = Object.values(ListSupportAdminTicketsTierEnum);
const PRIORITIES = Object.values(ListSupportAdminTicketsPriorityEnum);

/** Badge tone per status, matching the merchant app's tone choices. */
const STATUS_TONE: Record<string, Tone> = {
  NEW: 'sky',
  OPEN: 'sky',
  PENDING_CUSTOMER: 'amber',
  UNDER_REVIEW: 'gray',
  PLANNED: 'green',
  DECLINED: 'gray',
  RESOLVED: 'green',
  CLOSED: 'gray',
};

/** Higher tiers stand out, because that is the whole point of the tier. */
const TIER_CLASS: Record<string, string> = {
  STARTER: 'text-gray-500',
  PROFESSIONAL: 'text-gray-700',
  BUSINESS: 'text-primary-700 font-medium',
  ENTERPRISE: 'text-amber-700 font-semibold',
};

/**
 * Relative deadline, e.g. "in 3h" or "4h late". Absolute times are in the detail view.
 *
 * The `h` / `m` units stay untranslated: this is the narrowest column on the screen and the
 * German equivalents (`Std.`, `Min.`) would wrap it, while the shorthand reads the same either
 * way. Only the surrounding phrase is translated.
 */
function dueLabel(
  dueAt: string | null,
  overdue: boolean,
  t: (key: string, vars?: Record<string, string | number>) => string,
): string {
  if (!dueAt) return '—';
  const deltaMinutes = Math.round(
    (new Date(dueAt).getTime() - Date.now()) / 60_000,
  );
  const abs = Math.abs(deltaMinutes);
  const value = abs >= 60 ? `${Math.round(abs / 60)}h` : `${abs}m`;
  return overdue || deltaMinutes < 0
    ? t('support.queue.dueLate', { value })
    : t('support.queue.dueIn', { value });
}

/**
 * The support queue.
 *
 * Default order is by first-response deadline, so the top of the list is always what is
 * most at risk rather than what happened most recently. The three counters above the
 * filters answer the only questions worth asking before picking a ticket: how much is
 * open, how much is nobody's, and how much is already late.
 */
export function SupportQueuePage() {
  const { t, locale } = useLocale();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState<string>('');
  const [tier, setTier] = useState<string>('');
  const [priority, setPriority] = useState<string>('');
  const [assignee, setAssignee] = useState<string>('');
  const [overdue, setOverdue] = useState(false);
  const [scope, setScope] = useState<ListSupportAdminTicketsScopeEnum>(
    ListSupportAdminTicketsScopeEnum.Open,
  );
  const [search, setSearch] = useState('');
  const [debouncedSearch, flushSearch] = useDebouncedValue(search);

  // Each dropdown value is narrowed to the generated enum before it becomes a filter, so
  // a stale option cannot reach the API as a value it rejects with a 400 nobody can read.
  const filters: SupportQueueFilters = {
    page,
    limit: PAGE_SIZE,
    scope,
    overdue: overdue || undefined,
    search: debouncedSearch.trim() || undefined,
    status: toFilterStatus(status),
    tier: toFilterTier(tier),
    priority: toFilterPriority(priority),
    assignee: assignee || undefined,
  };

  const ticketsQuery = useQuery({
    queryKey: ['support-tickets', filters],
    queryFn: () => fetchSupportTickets(filters),
    // Poll: a support queue that only updates on reload is a queue people stop trusting.
    // Safe to swap under the reader, unlike the ticket page: nothing on this screen is bound
    // to server state — every filter is local — so a poll can only repaint rows.
    refetchInterval: 30_000,
    // The filters are part of the query key, so typing in the search box mints a new key and
    // would otherwise drop the table for the spinner on every debounce tick. Keeping the
    // previous rows makes a filter change a repaint rather than a blank screen.
    placeholderData: keepPreviousData,
  });

  const agentsQuery = useQuery({
    queryKey: ['support-agents'],
    queryFn: fetchSupportAgents,
    staleTime: 5 * 60_000,
  });

  const tickets = ticketsQuery.data;
  const counts = tickets?.counts ?? {};

  const cards: { key: string; label: string; value: number; tone: Tone }[] = [
    {
      key: 'open',
      label: t('support.queue.cardOpen'),
      value: counts['open'] ?? 0,
      tone: 'teal',
    },
    {
      key: 'unassigned',
      label: t('support.queue.cardUnassigned'),
      value: counts['unassigned'] ?? 0,
      tone: 'amber',
    },
    {
      key: 'overdue',
      label: t('support.queue.cardOverdue'),
      value: counts['overdue'] ?? 0,
      tone: 'red',
    },
  ];

  return (
    <>
      <PageHead
        eyebrow={t('support.eyebrow')}
        title={t('support.queue.title')}
        lead={t('support.queue.lead')}
      />

      <PageBody>
        {/* Three across from 360px: stacked, the counters took a whole screen before the first
            ticket. Only the narrowest phones stack them, where "ÜBERFÄLLIG" no longer fits. */}
        <div className="grid grid-cols-1 min-[360px]:grid-cols-3 gap-2 sm:gap-4">
          {cards.map((card) => (
            <div key={card.key} className="surface p-3 sm:p-5 min-w-0">
              <div className="eyebrow text-gray-500 max-sm:text-[11px] max-sm:tracking-normal break-words">
                {card.label}
              </div>
              <div
                className={`text-2xl sm:text-3xl font-bold mt-1 tabular-nums ${TONE_TEXT[card.tone]}`}
              >
                {formatCount(card.value, locale)}
              </div>
            </div>
          ))}
        </div>

        <div className="surface p-4 flex flex-wrap gap-3 items-center">
          <input
            type="search"
            value={search}
            onChange={(entry) => setSearch(entry.target.value)}
            onKeyDown={(entry) => entry.key === 'Enter' && flushSearch()}
            placeholder={t('support.queue.searchPlaceholder')}
            aria-label={t('support.queue.searchLabel')}
            className="field-input flex-1 min-w-[min(220px,100%)]"
          />
          <select
            value={scope}
            aria-label={t('support.queue.scopeLabel')}
            onChange={(entry) => {
              setScope(
                entry.target.value === ListSupportAdminTicketsScopeEnum.All
                  ? ListSupportAdminTicketsScopeEnum.All
                  : ListSupportAdminTicketsScopeEnum.Open,
              );
              setPage(1);
            }}
            className="field-input w-auto grow sm:grow-0"
          >
            <option value="open">{t('support.queue.scopeOpen')}</option>
            <option value="all">{t('support.queue.scopeAll')}</option>
          </select>
          <select
            value={status}
            aria-label={t('support.queue.statusLabel')}
            onChange={(entry) => {
              setStatus(entry.target.value);
              setPage(1);
            }}
            className="field-input w-auto grow sm:grow-0"
          >
            <option value="">{t('support.queue.anyStatus')}</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {supportEnumLabel(t, 'status', s)}
              </option>
            ))}
          </select>
          <select
            value={tier}
            aria-label={t('support.queue.planLabel')}
            onChange={(entry) => {
              setTier(entry.target.value);
              setPage(1);
            }}
            className="field-input w-auto grow sm:grow-0"
          >
            <option value="">{t('support.queue.anyPlan')}</option>
            {TIERS.map((tierValue) => (
              <option key={tierValue} value={tierValue}>
                {supportEnumLabel(t, 'tier', tierValue)}
              </option>
            ))}
          </select>
          <select
            value={priority}
            aria-label={t('support.queue.priorityLabel')}
            onChange={(entry) => {
              setPriority(entry.target.value);
              setPage(1);
            }}
            className="field-input w-auto grow sm:grow-0"
          >
            <option value="">{t('support.queue.anyPriority')}</option>
            {PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {supportEnumLabel(t, 'priority', p)}
              </option>
            ))}
          </select>
          <select
            value={assignee}
            aria-label={t('support.queue.assigneeLabel')}
            onChange={(entry) => {
              setAssignee(entry.target.value);
              setPage(1);
            }}
            className="field-input w-auto grow sm:grow-0"
          >
            <option value="">{t('support.queue.anyone')}</option>
            <option value="unassigned">{t('support.queue.unassigned')}</option>
            {(agentsQuery.data ?? []).map((agent) => (
              <option key={agent.username} value={agent.username}>
                {agent.displayName}
              </option>
            ))}
          </select>
          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={overdue}
              onChange={(entry) => {
                setOverdue(entry.target.checked);
                setPage(1);
              }}
              className="w-4 h-4 rounded border-gray-300 accent-primary"
            />
            {t('support.queue.overdueOnly')}
          </label>
        </div>

        <div className="surface overflow-x-auto">
          {ticketsQuery.isLoading ? (
            <div className="p-8 text-center text-gray-500">
              {t('common.loading')}
            </div>
          ) : ticketsQuery.isError ? (
            <div className="p-4">
              <StatusBanner tone="error">
                {t('support.queue.loadError')}
              </StatusBanner>
            </div>
          ) : (tickets?.data.length ?? 0) === 0 ? (
            <div className="p-8 text-center text-gray-500">
              {t('support.queue.empty')}
            </div>
          ) : (
            // Below `sm`, one column: the ticket with its shop, status, assignee and due time
            // underneath — the facts that decide which ticket to open, instead of a subject cut
            // off at the screen edge and five columns a sideways scroll away.
            <table className="data-table table-fixed sm:table-auto sm:min-w-[720px]">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left px-4 py-3">
                    {t('support.queue.columnTicket')}
                  </th>
                  <th className="hidden sm:table-cell text-left px-4 py-3">
                    {t('support.queue.columnShop')}
                  </th>
                  <th className="hidden sm:table-cell text-left px-4 py-3">
                    {t('support.queue.columnStatus')}
                  </th>
                  <th className="hidden sm:table-cell text-left px-4 py-3">
                    {t('support.queue.columnPlan')}
                  </th>
                  <th className="hidden sm:table-cell text-left px-4 py-3">
                    {t('support.queue.columnAssignee')}
                  </th>
                  <th className="hidden sm:table-cell text-left px-4 py-3">
                    {t('support.queue.columnDue')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(tickets?.data ?? []).map((ticket) => (
                  <tr key={ticket.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 sm:max-w-xs">
                      <Link
                        to={`/support/${ticket.id}`}
                        className="text-gray-900 hover:text-primary-700"
                      >
                        <span className="font-medium block truncate">
                          {ticket.subject}
                        </span>
                      </Link>
                      <div className="text-gray-500 text-xs mt-0.5">
                        {ticket.reference} ·{' '}
                        {supportEnumLabel(t, 'category', ticket.category)}
                        {ticket.awaitingAgent ? (
                          <span className="text-amber-700">
                            {' '}
                            · {t('support.queue.needsReply')}
                          </span>
                        ) : null}
                      </div>
                      <div className="sm:hidden mt-1.5 space-y-1 text-xs">
                        <div className="text-gray-700 truncate">
                          {ticket.shopDomain}
                        </div>
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <Badge
                            tone={STATUS_TONE[ticket.status] ?? 'gray'}
                            title={ticket.status}
                          >
                            {supportEnumLabel(t, 'status', ticket.status)}
                          </Badge>
                          <span className="text-gray-700">
                            {ticket.assignee ?? (
                              <span className="text-amber-700">
                                {t('support.queue.unassignedCell')}
                              </span>
                            )}
                          </span>
                          <span
                            className={`tabular-nums ${
                              ticket.overdue
                                ? 'text-red-600 font-medium'
                                : 'text-gray-600'
                            }`}
                          >
                            {dueLabel(
                              ticket.firstResponseDueAt ?? null,
                              ticket.overdue,
                              t,
                            )}
                          </span>
                        </div>
                      </div>
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-gray-700 max-w-48 truncate">
                      {ticket.shopDomain}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3">
                      {/*
                        The raw token rides along in the `title`: the badge reads as prose now,
                        and an operator cross-checking a row against the database or Sentry still
                        gets `PENDING_CUSTOMER` out of it without leaving the page.
                      */}
                      <Badge
                        tone={STATUS_TONE[ticket.status] ?? 'gray'}
                        title={ticket.status}
                      >
                        {supportEnumLabel(t, 'status', ticket.status)}
                      </Badge>
                    </td>
                    <td
                      className={`hidden sm:table-cell px-4 py-3 ${
                        TIER_CLASS[ticket.tier ?? ''] ?? 'text-gray-400'
                      }`}
                    >
                      {ticket.tier
                        ? supportEnumLabel(t, 'tier', ticket.tier)
                        : t('support.queue.noPlan')}
                    </td>
                    <td className="hidden sm:table-cell px-4 py-3 text-gray-700">
                      {ticket.assignee ?? (
                        <span className="text-amber-700">
                          {t('support.queue.unassignedCell')}
                        </span>
                      )}
                    </td>
                    <td
                      className={`hidden sm:table-cell px-4 py-3 tabular-nums ${
                        ticket.overdue
                          ? 'text-red-600 font-medium'
                          : 'text-gray-600'
                      }`}
                    >
                      {dueLabel(
                        ticket.firstResponseDueAt ?? null,
                        ticket.overdue,
                        t,
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {(tickets?.total ?? 0) > PAGE_SIZE ? (
          <div className="flex justify-center gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              className="btn-neutral btn-sm"
            >
              {t('common.previous')}
            </button>
            <button
              disabled={page * PAGE_SIZE >= (tickets?.total ?? 0)}
              onClick={() => setPage((p) => p + 1)}
              className="btn-neutral btn-sm"
            >
              {t('common.nextPage')}
            </button>
          </div>
        ) : null}
      </PageBody>
    </>
  );
}
